<template>
  <div class="coaching-chat flex flex-col h-full max-w-4xl mx-auto p-4 md:p-6">
    <CoachingSubNav :active="isProfiling ? 'profiling' : 'chat'" />
    <header class="mb-4 shrink-0">
      <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <h1 class="text-2xl font-semibold text-[var(--color-dark)]">{{ pageTitle }}</h1>
        <Button
          v-if="session"
          :label="$t('Coaching.newSession')"
          icon="pi pi-refresh"
          outlined
          size="small"
          class="!border-[var(--color-orange)] !text-[var(--color-orange)]"
          @click="resetToSetup"
        />
      </div>
      <p v-if="mockMode" class="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-2">
        {{ $t('Coaching.mockModeBanner') }}
      </p>
      <div
        v-if="seedEnabled"
        class="mt-3 p-3 border border-amber-200 rounded-lg bg-amber-50/60"
      >
        <p class="text-xs text-amber-900 mb-2">{{ seedDevHint }}</p>
        <div class="flex flex-wrap gap-2">
          <Button
            :label="$t('Coaching.seedGood')"
            severity="success"
            size="small"
            outlined
            :loading="seeding"
            :disabled="seeding"
            @click="seedSession('good')"
          />
          <Button
            :label="$t('Coaching.seedBad')"
            severity="danger"
            size="small"
            outlined
            :loading="seeding"
            :disabled="seeding"
            @click="seedSession('bad')"
          />
          <Button
            :label="$t('Coaching.seedMixed')"
            severity="warning"
            size="small"
            outlined
            :loading="seeding"
            :disabled="seeding"
            @click="seedSession('mixed')"
          />
          <Button
            v-if="!isProfiling"
            :label="$t('Coaching.seedBoth')"
            severity="info"
            size="small"
            outlined
            :loading="seeding"
            :disabled="seeding"
            @click="seedSession('mixed', 'both')"
          />
        </div>
      </div>
      <p v-if="session" class="text-sm text-gray-600 mt-1">
        {{ isProfiling
          ? $t('Coaching.profilingDimensionProgress', {
            current: session.currentDimensionIndex + 1,
            total: session.totalDimensions,
            title: session.currentDimensionTitle || '',
          })
          : $t('Coaching.dimensionProgress', {
            current: session.currentDimensionIndex + 1,
            total: session.totalDimensions,
            title: session.currentDimensionTitle || '',
          }) }}
      </p>
    </header>

    <div v-if="loadingSetup" class="flex-1 flex items-center justify-center">
      <ProgressSpinner />
    </div>

    <div
      v-else-if="phase === 'setup' && isProfiling"
      class="flex-1 p-6 bg-gray-50 border border-gray-200 rounded-xl"
    >
      <h2 class="text-lg font-medium text-[var(--color-dark)] mb-1">{{ $t('Coaching.profilingSetupTitle') }}</h2>
      <p class="text-sm text-gray-600 mb-4">{{ $t('Coaching.profilingSetupHint') }}</p>
      <p v-if="profilingPreview?.profilingMode" class="text-sm text-[var(--color-liver-dogs)] bg-orange-50/40 border border-orange-200 rounded-lg px-3 py-2 mb-4">
        {{ $t('Coaching.profilingModeLabel', { mode: profilingModeLabel }) }}
      </p>

      <div v-if="profilingPreview?.skippedDimensions?.length" class="mb-4">
        <p class="text-sm font-medium text-gray-800 mb-2">{{ $t('Coaching.profilingSkippedTitle') }}</p>
        <ul class="text-sm text-gray-600 space-y-1 list-disc list-inside">
          <li v-for="item in profilingPreview.skippedDimensions" :key="item.dimensionValue">
            {{ item.dimensionTitle }}
            <span v-if="item.score">({{ item.score.toFixed(1) }}/5)</span>
          </li>
        </ul>
      </div>

      <div v-if="profilingPreview?.dimensionQueue?.length" class="mb-5">
        <p class="text-sm font-medium text-gray-800 mb-2">{{ $t('Coaching.profilingQueueTitle') }}</p>
        <ul class="text-sm text-gray-600 space-y-1 list-disc list-inside">
          <li v-for="item in profilingPreview.dimensionQueue" :key="item.value">
            {{ item.title }}
          </li>
        </ul>
      </div>

      <Button
        class="btn-primary-accent"
        unstyled
        :label="$t('Coaching.startProfiling')"
        icon="pi pi-play"
        :loading="loading"
        :disabled="!profilingPreview?.dimensionQueue?.length"
        @click="startSession"
      />
    </div>

    <div
      v-else-if="phase === 'setup'"
      class="flex-1 p-6 bg-gray-50 border border-gray-200 rounded-xl"
    >
      <h2 class="text-lg font-medium text-[var(--color-dark)] mb-1">{{ $t('Coaching.setupTitle') }}</h2>
      <p class="text-sm text-gray-600 mb-5">{{ $t('Coaching.setupHint') }}</p>

      <div class="space-y-4 max-w-lg">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            {{ $t('Coaching.selectDimension') }}
          </label>
          <Select
            v-model="selectedDimensionValue"
            :options="dimensions"
            option-label="title"
            option-value="value"
            class="w-full"
            :placeholder="$t('Coaching.selectDimensionPlaceholder')"
          />
          <p v-if="selectedDimension?.description" class="text-xs text-gray-500 mt-2">
            {{ selectedDimension.description }}
          </p>
        </div>

        <div class="flex items-start gap-2">
          <Checkbox
            v-model="singleDimensionOnly"
            input-id="single-dimension"
            :binary="true"
          />
          <label for="single-dimension" class="text-sm text-gray-700 cursor-pointer">
            {{ $t('Coaching.singleDimensionOnly') }}
          </label>
        </div>
        <p v-if="!singleDimensionOnly && selectedDimensionValue" class="text-xs text-gray-500 -mt-2">
          {{ $t('Coaching.multiDimensionHint') }}
        </p>

        <Button
          class="btn-primary-accent"
          unstyled
          :label="$t('Coaching.startSession')"
          icon="pi pi-play"
          :disabled="!selectedDimensionValue"
          :loading="loading"
          @click="startSession"
        />
      </div>
    </div>

    <div v-else-if="error && !session" class="p-4 bg-red-50 border border-red-200 rounded-lg">
      <p class="text-red-800">{{ error }}</p>
      <Button class="mt-3" :label="$t('Coaching.retry')" @click="resetToSetup" />
    </div>

    <template v-else-if="session">
      <div
        ref="messagesContainer"
        class="flex-1 overflow-y-auto space-y-4 mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200 min-h-[320px]"
      >
        <div
          v-for="(message, index) in messages"
          :key="index"
          class="flex"
          :class="message.role === 'user' ? 'justify-end' : 'justify-start'"
        >
          <div
            class="max-w-[85%] rounded-2xl px-4 py-3 text-sm whitespace-pre-wrap"
            :class="message.role === 'user'
              ? 'bg-[var(--color-orange)] text-white'
              : 'bg-white border border-gray-200 text-gray-800 shadow-sm'"
          >
            <span class="whitespace-pre-wrap">{{ message.content }}</span>
          </div>
        </div>

        <div v-if="isStreaming" class="flex justify-start">
          <div class="bg-white border border-gray-200 rounded-2xl px-4 py-3 text-sm text-gray-800 shadow-sm max-w-[85%] whitespace-pre-wrap">
            {{ streamingText }}<span class="animate-pulse">▍</span>
          </div>
        </div>

        <div v-if="session.isChapterReady && session.status === 'active'" class="text-center py-2">
          <span class="inline-flex items-center gap-2 text-sm text-[var(--color-liver-dogs)] bg-orange-50/40 border border-orange-200 px-3 py-2 rounded-full">
            <i class="pi pi-check-circle" />
            {{ isProfiling ? $t('Coaching.profilingReadyForNext') : $t('Coaching.readyForNext') }}
          </span>
        </div>

        <div v-if="session.status === 'completed' && profileFeatureVisible" class="text-center py-6">
          <p class="text-lg font-medium text-[var(--color-dark)]">{{ $t('Coaching.sessionComplete') }}</p>
          <router-link to="/coaching/profile" class="text-[var(--color-orange)] hover:underline text-sm mt-2 inline-block">
            {{ $t('Coaching.viewProfile') }}
          </router-link>
        </div>
      </div>

      <form
        v-if="session.status === 'active'"
        class="shrink-0 flex gap-2"
        @submit.prevent="handleSend"
      >
        <InputText
          v-model="draft"
          class="flex-1"
          :placeholder="isProfiling ? $t('Coaching.profilingInputPlaceholder') : $t('Coaching.inputPlaceholder')"
          :disabled="isStreaming || sending"
          autocomplete="off"
        />
        <Button
          type="submit"
          icon="pi pi-send"
          class="!border-[var(--color-orange)] !text-[var(--color-orange)]"
          outlined
          :loading="sending || isStreaming"
          :disabled="!draft.trim() || isStreaming"
          :aria-label="$t('Coaching.send')"
        />
      </form>
    </template>
  </div>
</template>

<script>
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import InputText from 'primevue/inputtext';
import ProgressSpinner from 'primevue/progressspinner';
import Select from 'primevue/select';
import CoachingSubNav from '@/components/Home/Coaching/CoachingSubNav.vue';
import coachingService from '@/services/coachingService';
import { isFeatureVisibleForCustomer } from '@/utils/featureAccess';

export default {
  name: 'CoachingChatComponent',
  props: {
    sessionMode: {
      type: String,
      default: 'coaching',
      validator: (value) => ['coaching', 'profiling'].includes(value),
    },
  },
  components: {
    Button,
    Checkbox,
    InputText,
    ProgressSpinner,
    Select,
    CoachingSubNav,
  },
  data() {
    return {
      phase: 'setup',
      loadingSetup: true,
      loading: false,
      sending: false,
      isStreaming: false,
      streamingText: '',
      error: null,
      session: null,
      messages: [],
      draft: '',
      mockMode: false,
      seedEnabled: false,
      seeding: false,
      dimensions: [],
      selectedDimensionValue: null,
      singleDimensionOnly: true,
      profilingPreview: null,
    };
  },
  computed: {
    isProfiling() {
      return this.sessionMode === 'profiling';
    },
    pageTitle() {
      return this.isProfiling ? this.$t('Coaching.profilingTitle') : this.$t('Coaching.title');
    },
    profilingModeLabel() {
      const mode = this.profilingPreview?.profilingMode;
      const labels = {
        full: this.$t('Coaching.profilingModeFull'),
        skip_covered: this.$t('Coaching.profilingModeSkip'),
        shortened: this.$t('Coaching.profilingModeShortened'),
      };
      return labels[mode] || mode;
    },
    selectedDimension() {
      return this.dimensions.find((d) => d.value === this.selectedDimensionValue);
    },
    profileFeatureVisible() {
      return isFeatureVisibleForCustomer(this.$store, 'llmCoachingProfile');
    },
    seedDevHint() {
      if (this.isProfiling) {
        return this.$t('Coaching.seedDevHintProfiling');
      }
      return this.$t('Coaching.seedDevHint');
    },
  },
  mounted() {
    this.initPage();
  },
  methods: {
    async initPage() {
      this.loadingSetup = true;
      try {
        await Promise.all([
          this.loadStatus(),
          this.isProfiling ? this.loadProfilingPreview() : this.loadDimensions(),
        ]);
      } finally {
        this.loadingSetup = false;
      }
    },
    async loadStatus() {
      try {
        const status = await coachingService.getStatus();
        this.mockMode = Boolean(status?.mockMode);
        this.seedEnabled = Boolean(status?.seedEnabled);
      } catch {
        this.mockMode = false;
        this.seedEnabled = false;
      }
    },
    async loadDimensions() {
      try {
        this.dimensions = await coachingService.listDimensions();
        if (this.dimensions.length && !this.selectedDimensionValue) {
          this.selectedDimensionValue = this.dimensions[0].value;
        }
      } catch {
        this.dimensions = [];
      }
    },
    async loadProfilingPreview() {
      try {
        this.profilingPreview = await coachingService.previewProfiling();
      } catch {
        this.profilingPreview = null;
      }
    },
    resetToSetup() {
      this.phase = 'setup';
      this.session = null;
      this.messages = [];
      this.error = null;
      this.draft = '';
    },
    async seedSession(preset, sessionType) {
      this.seeding = true;
      try {
        const resolvedType = sessionType ?? (this.isProfiling ? 'profiling' : 'coaching');
        const data = await coachingService.seedSession(preset, resolvedType);
        this.$toast?.add?.({
          severity: 'success',
          summary: this.$t('Coaching.seedSuccess', { count: data.scores?.length ?? 0 }),
          life: 4000,
        });
        await this.$router.push('/coaching/profile');
      } catch (err) {
        this.$toast?.add?.({
          severity: 'error',
          summary: err?.response?.data?.message || err?.message || this.$t('Coaching.seedFailed'),
          life: 5000,
        });
      } finally {
        this.seeding = false;
      }
    },
    async startSession() {
      if (!this.isProfiling && !this.selectedDimensionValue) return;
      this.loading = true;
      this.error = null;
      try {
        const data = this.isProfiling
          ? await coachingService.createProfilingSession()
          : await coachingService.createSession({
            startDimensionValue: this.selectedDimensionValue,
            singleDimensionOnly: this.singleDimensionOnly,
          });
        this.phase = 'chat';
        this.session = data.session;
        this.messages = [...(data.session?.messages ?? [])];
        if (!this.messages.length && data.message) {
          this.messages.push({ role: 'assistant', content: data.message });
          this.session = { ...this.session, isChapterReady: data.isChapterReady };
        }
        this.$nextTick(this.scrollToBottom);
      } catch (err) {
        this.error = err?.response?.data?.message || err?.message
          || (this.isProfiling ? this.$t('Coaching.profilingStartFailed') : this.$t('Coaching.startFailed'));
      } finally {
        this.loading = false;
      }
    },
    async handleSend() {
      const content = this.draft.trim();
      if (!content || !this.session?.id || this.sending) return;

      if (content.toLowerCase() === '/next') {
        await this.handleAdvance();
        return;
      }

      this.messages.push({ role: 'user', content });
      this.draft = '';
      this.sending = true;
      this.isStreaming = true;
      this.streamingText = '';
      this.$nextTick(this.scrollToBottom);

      try {
        await coachingService.sendMessageStream(this.session.id, content, {
          onToken: (token) => {
            this.streamingText += token;
            this.scrollToBottom();
          },
          onDone: (result) => {
            if (this.streamingText || result.message) {
              this.messages.push({
                role: 'assistant',
                content: result.message || this.streamingText,
              });
            }
            this.session = { ...this.session, isChapterReady: result.isChapterReady };
            this.streamingText = '';
            this.isStreaming = false;
            this.$nextTick(this.scrollToBottom);
          },
          onError: (payload) => {
            throw new Error(payload?.message || 'Stream failed');
          },
        });
      } catch (err) {
        this.isStreaming = false;
        this.streamingText = '';
        this.messages.push({
          role: 'assistant',
          content: err?.message || this.$t('Coaching.messageFailed'),
        });
      } finally {
        this.sending = false;
      }
    },
    async handleAdvance() {
      if (!this.session?.id) return;
      this.sending = true;
      this.draft = '';
      try {
        const data = await coachingService.advanceDimension(this.session.id);
        this.session = data.session;
        this.messages = [...(data.session?.messages ?? [])];
        if (data.openingMessage) {
          this.messages.push({ role: 'assistant', content: data.openingMessage });
        }
        if (data.score) {
          this.messages.push({
            role: 'assistant',
            content: this.$t('Coaching.dimensionScored', {
              title: data.score.dimensionTitle,
              score: data.score.score.toFixed(1),
              interpretation: data.score.interpretation,
            }),
          });
        }
        if (data.completed) {
          this.$toast?.add?.({
            severity: 'success',
            summary: this.isProfiling
              ? this.$t('Coaching.profilingSessionComplete')
              : this.$t('Coaching.sessionComplete'),
            life: 4000,
          });
        }
        this.$nextTick(this.scrollToBottom);
      } catch (err) {
        this.messages.push({
          role: 'assistant',
          content: err?.response?.data?.message || err?.message || this.$t('Coaching.advanceFailed'),
        });
      } finally {
        this.sending = false;
      }
    },
    scrollToBottom() {
      const el = this.$refs.messagesContainer;
      if (el) el.scrollTop = el.scrollHeight;
    },
  },
};
</script>
